/**
 * レイヤー一覧 (docs/features/motion-graphics-mode-plan.md §5.2)。MotionLayerModal の中身。
 * 合成プレビュー (左 / モバイルでは上) とレイヤー行 (右 / 下)、固定の説明と warnings を出す。
 *
 * - 各行: 小さなサムネイル (合成 PNG をレイヤーの矩形で切り抜いた簡易表示)・名前 (クリックで編集・
 *   Enter / blur で確定・Esc で取り消し)・塗り色のチップ (最大 4 色)・固定チェック
 * - ラスタのレイヤー (kind 'png' = 画像を含むレイヤー) は塗り色のチップの代わりに小さなバッジ「画像」を出す
 *   (色の一時変更・線の描画は使えない。サムネイルは SVG と同じく合成 PNG の切り抜き)
 * - 上部の合成プレビューは、行にホバー / フォーカスしたレイヤーの矩形をハイライトする
 * - 解析時の warnings を最後に出す (成功時の warnings はトーストにしない)
 *
 * 名前の上書きと固定は useMotionLayerEditsStore (素材 id ごと・sessionStorage に永続化) が正本。
 * 名前は素材には保存せず、generate の body (`layer_names` / `locked_layer_indexes`) に載せる (layeredGenerate.ts)。
 */
import { useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import type { GenerationAsset } from '../state/types';
import {
  isCheckedLayerStack,
  isCollapsibleLayer,
  layerRectPercent,
  layerStackFromMaterial,
  layerThumbnailStyle,
  RASTER_LAYER_BADGE_LABEL,
  RASTER_LAYER_BADGE_TITLE,
  type LayerStack,
  type LayerStackLayer,
} from '../../../features/motion/layerStack';
import { normalizeLayerName, useMotionLayerEditsStore } from '../../../features/motion/layerEditsStore';
import { restructureMaterialLayers, type LayerRestructureAction } from '../../../features/materials/api';
import { extractLayeredIntakeError } from '../../../features/motion/layeredIntakeErrors';
import { motionLayeredRequestError } from '../../../features/motion/layeredGenerate';
import { useGenerationAssetsSafe } from '../state/assetsContext';
import { useToast } from '../../../hooks/useToast';
import styles from '../scenario/ScenarioGenerator.module.css';

const THUMB_BOX = 36;
const MAX_FILL_CHIPS = 4;
const HEX_COLOR = /^#[0-9a-f]{6}$/i;
const EMPTY_NAMES: Record<number, string> = {};
const EMPTY_LOCKED: number[] = [];

/** IME 変換確定の Enter か (既存の isComposingEnter と同じ判定・Safari は keyCode 229 のみ) */
function isComposingEnter(event: KeyboardEvent<HTMLElement>): boolean {
  return event.nativeEvent.isComposing || event.keyCode === 229;
}

type Props = {
  asset: GenerationAsset;
  stack: LayerStack;
  /** 合成プレビューの最大の高さ (CSS の長さ)。モーダルでは 60vh */
  previewMaxHeight?: string;
};

export function MotionLayerList({ asset, stack, previewMaxHeight = '240px' }: Props) {
  const nameOverrides = useMotionLayerEditsStore((s) => s.names[asset.id] ?? EMPTY_NAMES);
  const locked = useMotionLayerEditsStore((s) => s.locked[asset.id] ?? EMPTY_LOCKED);
  const renameLayer = useMotionLayerEditsStore((s) => s.renameLayer);
  const toggleLayerLock = useMotionLayerEditsStore((s) => s.toggleLayerLock);
  const clearMaterialEdits = useMotionLayerEditsStore((s) => s.clearMaterial);
  const assetsContext = useGenerationAssetsSafe();
  const toast = useToast();
  // グループの展開 / 戻す (元 SVG の再解析。数秒〜数十秒かかるので処理中は行の操作を止める)
  const [restructuring, setRestructuring] = useState<number | null>(null);
  const restructure = async (layer: LayerStackLayer, action: LayerRestructureAction) => {
    if (restructuring !== null || !assetsContext) return;
    setRestructuring(layer.index);
    try {
      const row = await restructureMaterialLayers(asset.type === 'cloud' ? 'cloud' : 'temporary', asset.id, {
        action, layer_index: layer.index,
      });
      const next = layerStackFromMaterial(row);
      if (!next || !isCheckedLayerStack(next)) {
        toast.error('レイヤー構成を読み込めませんでした。SVG をアップロードし直してください');
        return;
      }
      // レイヤー番号が変わるので、この素材の名前上書き・固定は消す
      clearMaterialEdits(asset.id);
      assetsContext.replaceAssets(
        assetsContext.selectedAssets.map((a) => (a.id === asset.id ? { ...a, layerStack: next } : a)),
        { silent: true },
      );
      for (const warning of row.warnings ?? []) toast.show(warning);
      toast.show(action === 'expand' ? `グループを展開しました (${next.layers.length} レイヤー)` : `グループに戻しました (${next.layers.length} レイヤー)`);
    } catch (error) {
      console.error('Failed to restructure layers', error);
      const intake = extractLayeredIntakeError(error);
      toast.error(intake ? intake.message : motionLayeredRequestError(error).message);
    } finally {
      setRestructuring(null);
    }
  };
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  // Esc で取り消した直後に input のアンマウントで走る onBlur が古い draft を確定しないための印
  const cancelledRef = useRef(false);

  const previewUrl = asset.sourceUrl || asset.thumbnailUrl;
  const displayName = (layer: LayerStackLayer) => nameOverrides[layer.index] ?? layer.name;
  // 一覧は上のレイヤーから並べる (制作ツールのレイヤーパネルと同じ向き)。index は重ね順 (下 → 上)
  const rows = [...stack.layers].sort((a, b) => b.index - a.index);
  const hoveredLayer = hoveredIndex === null ? null : stack.layers.find((l) => l.index === hoveredIndex) ?? null;
  const highlight = hoveredLayer ? layerRectPercent(stack, hoveredLayer) : null;

  const startEdit = (layer: LayerStackLayer) => {
    cancelledRef.current = false;
    setEditingIndex(layer.index);
    setDraft(displayName(layer));
  };
  const commitEdit = (layer: LayerStackLayer) => {
    if (cancelledRef.current || editingIndex !== layer.index) return;
    const normalized = normalizeLayerName(draft);
    // 空欄・元の名前と同じなら上書きを消す (元の名前に戻す)
    renameLayer(asset.id, layer.index, normalized === null || normalized === layer.name ? null : normalized);
    setEditingIndex(null);
  };

  // 合成プレビューは比率を保ったまま、高さ previewMaxHeight・幅 100% に収める
  // (幅を min(100%, 最大高さ × 比率) で決めて高さは aspect-ratio。max-height で潰すとハイライトの % がずれる)
  const previewStyle = {
    '--motion-layer-aspect': `${stack.composite.width} / ${stack.composite.height}`,
    '--motion-layer-ratio': String(stack.composite.width / stack.composite.height),
    '--motion-layer-preview-max-h': previewMaxHeight,
  } as CSSProperties;

  return (
    <div className={styles.motionLayerList} data-testid="motion-layer-list">
      <div className={styles.motionLayerBody}>
      {previewUrl && (
        <div className={styles.motionLayerPreviewColumn}>
          <div
            className={styles.motionLayerPreview}
            style={previewStyle}
            data-testid="motion-layer-preview"
          >
            <img src={previewUrl} alt="合成プレビュー" draggable={false} />
            {highlight && (
              <div
                className={styles.motionLayerHighlight}
                data-testid="motion-layer-highlight"
                style={{
                  left: `${highlight.left}%`,
                  top: `${highlight.top}%`,
                  width: `${highlight.width}%`,
                  height: `${highlight.height}%`,
                }}
              />
            )}
          </div>
        </div>
      )}
      <ul className={styles.motionLayerRows}>
        {rows.map((layer) => {
          const name = displayName(layer);
          const isLocked = locked.includes(layer.index);
          const isActive = hoveredIndex === layer.index;
          const isRaster = layer.kind === 'png';
          const fills = isRaster ? [] : layer.fills.filter((c) => HEX_COLOR.test(c)).slice(0, MAX_FILL_CHIPS);
          const thumb = layerThumbnailStyle(stack, layer, THUMB_BOX);
          return (
            <li
              key={layer.index}
              className={`${styles.motionLayerRow} ${isActive ? styles.motionLayerRowActive : ''}`}
              data-testid={`motion-layer-row-${layer.index}`}
              onMouseEnter={() => setHoveredIndex(layer.index)}
              onMouseLeave={() => setHoveredIndex((cur) => (cur === layer.index ? null : cur))}
              onFocus={() => setHoveredIndex(layer.index)}
              onBlur={(e) => {
                // 行の外へフォーカスが移ったらハイライトを消す (行内の input / checkbox 間の移動は維持)
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                  setHoveredIndex((cur) => (cur === layer.index ? null : cur));
                }
              }}
            >
              <span className={styles.motionLayerThumb} aria-hidden="true">
                {previewUrl && (
                  <img
                    src={previewUrl}
                    alt=""
                    draggable={false}
                    style={{ width: thumb.width, height: thumb.height, left: thumb.left, top: thumb.top }}
                  />
                )}
              </span>
              {editingIndex === layer.index ? (
                <input
                  className={styles.motionLayerNameInput}
                  value={draft}
                  autoFocus
                  maxLength={80}
                  aria-label="レイヤー名"
                  onChange={(e) => setDraft(e.target.value)}
                  onBlur={() => commitEdit(layer)}
                  onKeyDown={(e) => {
                    // IME 変換確定の Enter では確定しない
                    if (e.key === 'Enter' && !isComposingEnter(e)) {
                      e.preventDefault();
                      commitEdit(layer);
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      cancelledRef.current = true;
                      setEditingIndex(null);
                    }
                  }}
                />
              ) : (
                <button
                  type="button"
                  className={styles.motionLayerName}
                  title="クリックで名前を変更"
                  onClick={() => startEdit(layer)}
                >
                  {name || `レイヤー ${layer.index + 1}`}
                </button>
              )}
              {isRaster && (
                <span
                  className={styles.motionLayerRasterBadge}
                  title={RASTER_LAYER_BADGE_TITLE}
                  data-testid="motion-layer-raster-badge"
                >
                  {RASTER_LAYER_BADGE_LABEL}
                </span>
              )}
              {fills.length > 0 && (
                <span className={styles.motionLayerFills}>
                  {fills.map((color) => (
                    <span
                      key={color}
                      className={styles.motionLayerFill}
                      style={{ background: color }}
                      title={color}
                      data-testid="motion-layer-fill"
                    />
                  ))}
                </span>
              )}
              <label className={styles.motionLayerLock}>
                <input
                  type="checkbox"
                  checked={isLocked}
                  onChange={() => toggleLayerLock(asset.id, layer.index)}
                  aria-label={`${name} を固定`}
                />
                固定
              </label>
              {assetsContext && layer.expandable && (
                <button
                  type="button"
                  className={styles.motionLayerRestructure}
                  onClick={() => { void restructure(layer, 'expand'); }}
                  disabled={restructuring !== null}
                  title="このグループの中身を 1 段ずつ別のレイヤーにします"
                  data-testid={`motion-layer-expand-${layer.index}`}
                >
                  {restructuring === layer.index ? '解析中…' : 'グループを展開'}
                </button>
              )}
              {assetsContext && isCollapsibleLayer(stack, layer) && (
                <button
                  type="button"
                  className={styles.motionLayerRestructure}
                  onClick={() => { void restructure(layer, 'collapse'); }}
                  disabled={restructuring !== null}
                  title="展開したグループを 1 つのレイヤーに戻します"
                  data-testid={`motion-layer-collapse-${layer.index}`}
                >
                  {restructuring === layer.index ? '解析中…' : 'グループに戻す'}
                </button>
              )}
            </li>
          );
        })}
      </ul>
      </div>
      <p className={styles.motionLayerNote}>固定したレイヤーは動かしません。名前は動きの指示で使えます</p>
      {stack.warnings.length > 0 && (
        <ul className={styles.motionLayerWarnings} data-testid="motion-layer-warnings">
          {stack.warnings.map((w, i) => (
            <li key={`${i}-${w}`}>{w}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
